import React, { useState, useEffect, useRef } from 'react';
import { useTopSearch } from '../lib/pageSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   AppHeader — redesigned shared top bar (warm-cream + sage)
   Renders on top-level routes only (hidden on project-detail, where
   ProjectHeader takes over). Left: page identity. Center/right: the search
   for the page on screen (lib/pageSearch — Duncan 2026-10-17: it filters the
   page you're on, never a project jump; no box on a page with nothing to
   search). Far right: optional page action + notifications bell. Token-only colors.
   ═══════════════════════════════════════════════════════════════════════════ */

const FOCUS_RING = '0 0 0 3px var(--accent-ring)';

/* ─────────────── Inline SVG icons ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {d}
    </svg>
  );
  switch (name) {
    case 'search':
      return p(
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </>
      );
    case 'plus':
      return p(<path d="M12 5v14M5 12h14" />);
    case 'bell':
      return p(
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </>
      );
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'enter':
      return p(
        <>
          <path d="M9 10 4 15l5 5" />
          <path d="M20 4v7a4 4 0 0 1-4 4H4" />
        </>
      );
    case 'chevron-r':
      return p(<path d="m9 6 6 6-6 6" />);
    case 'building':
      return p(
        <>
          <path d="M3 21h18" />
          <path d="M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16" />
          <path d="M14 21V9h4a1 1 0 0 1 1 1v11" />
          <path d="M9 8h2M9 12h2M9 16h2" />
        </>
      );
    default:
      return null;
  }
};

/* ─────────────── Search field ─────────────── */

export const SearchField = React.forwardRef(function SearchField(
  { value, placeholder, focused, onFocus, onBlur, onChange, onKeyDown, onClear },
  ref
) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        width: '100%',
        padding: '0 8px 0 11px',
        borderRadius: 8,
        background: focused ? 'var(--bg-surface)' : 'var(--bg-surface-2)',
        border: `1px solid ${focused ? 'var(--accent)' : 'var(--border-default)'}`,
        boxShadow: focused ? FOCUS_RING : 'none',
        transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
      }}
    >
      <span style={{ color: focused ? 'var(--accent-text)' : 'var(--text-3)', flex: '0 0 auto', display: 'inline-flex', transition: 'color 120ms' }}>
        <Icon name="search" size={15} />
      </span>
      <input
        ref={ref}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 0,
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontSize: 14.5,
          color: 'var(--text-1)',
          letterSpacing: '-0.006em',
        }}
      />
      {value ? (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onClear();
          }}
          aria-label="Clear search"
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            flex: '0 0 auto',
            color: 'var(--text-3)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="x" size={14} />
        </button>
      ) : (
        <span
          className="mono"
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            minWidth: 18,
            height: 20,
            padding: '0 6px',
            borderRadius: 5,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--text-3)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          /
        </span>
      )}
    </div>
  );
});

/* ─────────────── Identity (title + breadcrumb) ─────────────── */

const Identity = ({ title, crumbs }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
    {crumbs && crumbs.length > 0 && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text-3)', marginBottom: 1 }}>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            <span style={{ whiteSpace: 'nowrap' }}>{c}</span>
            <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}>
              <Icon name="chevron-r" size={12} sw={1.8} />
            </span>
          </React.Fragment>
        ))}
      </div>
    )}
    <h1 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
      {title}
    </h1>
  </div>
);

/* ─────────────── Bell ─────────────── */

const Bell = ({ count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
    style={{
      position: 'relative',
      width: 36,
      height: 36,
      borderRadius: 8,
      flex: '0 0 auto',
      color: 'var(--text-2)',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: 'var(--shadow-xs)',
      cursor: 'pointer',
    }}
  >
    <Icon name="bell" size={17} />
    {count > 0 && (
      <span
        className="mono"
        style={{
          position: 'absolute',
          top: -5,
          right: -5,
          minWidth: 17,
          height: 17,
          padding: '0 4px',
          borderRadius: 999,
          background: 'var(--danger)',
          color: '#fff',
          fontSize: 11.5,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid var(--bg-surface)',
          lineHeight: 1,
        }}
      >
        {count > 99 ? '99+' : count}
      </span>
    )}
  </button>
);

/* ─────────────── media query hook ─────────────── */

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/* ═══════════════════════════════════════════════════════════════════════════
   AppHeader
   ═══════════════════════════════════════════════════════════════════════════ */

/** The bar's search box for the page on screen; null when the page has nothing to search.
 *  "/" focuses it, Escape clears it; Enter does nothing (the page filters as you type). */
export function useTopSearchField({ onSlash } = {}) {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const search = useTopSearch();
  const placeholder = search && search.owner ? search.owner.placeholder : null;
  useEffect(() => {
    if (!placeholder) return undefined;
    const onKey = (e) => {
      if (e.key !== '/') return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
      e.preventDefault();
      if (onSlash) onSlash();
      // focus on next tick so the field exists if it was collapsed
      requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placeholder, onSlash]);
  if (!placeholder) return { field: null, inputRef };
  const field = (extra = {}) => (
    <SearchField
      ref={inputRef}
      value={search.query}
      placeholder={placeholder}
      focused={focused}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (extra.onBlur) extra.onBlur();
      }}
      onChange={search.setQuery}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          search.setQuery('');
          if (inputRef.current) inputRef.current.blur();
        } else if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      onClear={() => {
        search.setQuery('');
        if (inputRef.current) inputRef.current.focus();
      }}
    />
  );
  return { field, inputRef, placeholder };
}

export default function AppHeader({ title, crumbs = null, action = null, onBellClick, notifCount = 0 }) {
  const [narrowOpen, setNarrowOpen] = useState(false);
  const narrow = useMediaQuery('(max-width: 860px)');
  const { field, inputRef, placeholder } = useTopSearchField({ onSlash: narrow ? () => setNarrowOpen(true) : null });

  const searchContainer = field && (
    <div style={{ position: 'relative', width: narrow ? '100%' : 'clamp(280px, 30vw, 360px)', flex: narrow ? '1 1 auto' : '0 1 360px' }}>
      {field({ onBlur: () => { if (narrow) setNarrowOpen(false); } })}
    </div>
  );

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flex: '0 0 56px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        height: 56,
        padding: '0 20px',
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* When narrow + search expanded, the field overlays the bar full-width */}
      {narrow && narrowOpen && searchContainer ? (
        searchContainer
      ) : (
        <>
          <Identity title={title} crumbs={crumbs} />
          <div style={{ flex: 1, minWidth: 12 }} />
          {narrow ? (
            field && (
              <button
                type="button"
                aria-label={placeholder}
                onClick={() => {
                  setNarrowOpen(true);
                  requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
                }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  flex: '0 0 auto',
                  color: 'var(--text-2)',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-default)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Icon name="search" size={16} />
              </button>
            )
          ) : (
            searchContainer
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
            {action}
            {onBellClick && <Bell count={notifCount} onClick={onBellClick} />}
          </div>
        </>
      )}
    </header>
  );
}
